/**
 * SemanticSearchBar — Semantic search input for ProposalsPage
 *
 * Story BL-045.3: Semantic Content Search (AC7)
 *
 * Features:
 * - Search input triggered on Enter key or Search button click
 * - "Semantic" badge when semantic=true in response
 * - "Clear" button to reset to standard proposal list
 * - Loading state during fetch
 *
 * data-testid attributes:
 *   semantic-search-input, semantic-mode-badge, semantic-clear-btn
 */

import { useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { semanticSearch, type SearchResult } from '../../services/searchService';

export interface SemanticSearchBarProps {
  onResults: (results: SearchResult[], semantic: boolean) => void;
  onClear: () => void;
}

export function SemanticSearchBar({ onResults, onClear }: SemanticSearchBarProps) {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isSemantic, setIsSemantic] = useState(false);

  async function handleSearch() {
    const trimmed = query.trim();
    if (trimmed.length < 3) return;

    setIsLoading(true);

    try {
      const response = await semanticSearch(trimmed, 'proposal');
      setIsActive(true);
      setIsSemantic(response.semantic);
      onResults(response.results, response.semantic);
    } catch {
      // Silently fail — keep current state
    } finally {
      setIsLoading(false);
    }
  }

  function handleClear() {
    setQuery('');
    setIsActive(false);
    setIsSemantic(false);
    onClear();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search proposals by meaning..."
            className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            data-testid="semantic-search-input"
          />
        </div>

        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        ) : (
          <button
            onClick={handleSearch}
            disabled={query.trim().length < 3}
            className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
          >
            Search
          </button>
        )}

        {isActive && (
          <>
            {isSemantic && (
              <span
                className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                data-testid="semantic-mode-badge"
              >
                Semantic
              </span>
            )}
            <button
              onClick={handleClear}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              data-testid="semantic-clear-btn"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          </>
        )}
      </div>
    </div>
  );
}
